import apiDigitacao from "api/api-digitacao";
import { CustomModal } from "components/custom-modal";
import { useFormFields } from "hooks/use-form-fields";
import { createModalTrigger, useModalControls } from "hooks/use-modal-controls";
import {
  copyAttendanceDataToDigitacaoConsigCLT,
  validateDigitacaoCLT,
} from "./utils";
import { useAttendanceContext } from "contexts/attendance-context";
import { Attendance } from "components/atendimentos-components/atendimento-form/types";
import { Toast } from "components/toast";
import { useEffect, useState } from "react";
import { Button, Center, Flex, Grid, Text } from "@chakra-ui/react";
import { CloseIcon } from "components/vectors/close-icon";
import { bancosConsigCLT } from "..";
import { CopyIcon, Search2Icon } from "@chakra-ui/icons";
import { InputField } from "components/input-field";
import {
  maskCEPFormat,
  maskCPFFormat,
  maskPhoneFormat,
} from "utils/string-formats";
import { DropdownField } from "components/dropdown-field";
import { dropdownTipoConta } from "../../../digitacao/modal-digitar-proposta/utils";
import { factaCitiesArray } from "components/atendimentos-components/atendimento-form/step-form/tabs-components/digitacao-fields/parts/cities-array";
import { InputSearch } from "components/input-search";
import { ButtonWithLoading } from "components/mailing-components/mailing-table/icon-button-with-loading";
import { normalizeText } from "utils/filter-array-by";
import {
  CEPProps,
  getCEPData,
} from "components/atendimentos-components/atendimento-form/step-form/tabs-components/digitacao-fields/parts/const";
import api from "api/api";
import { useApplicationContext } from "contexts/ApplicationContext";
import { isUserGerente } from "utils/is-gerente";
import { DropdownOptionProps } from "components/dropdown";
import { DigitadoRow } from "../../../digitacao/digitados-part";
import { AttendanceTabKey } from "components/atendimentos-components/pagina-atendimento/attendance-data-form";

const eventName = "openModalDigitacaoConsigCLT";

interface ModalParams {
  bancoDigitacao: string;
  digitado?: DigitadoRow;
}

export interface DigitacaoCLTProps {
  bancoDigitacao: string;
  cpf: string;
  name: string;
  phoneDDD1: string;
  phoneNumber1: string;
  documentNumber: string;
  bankNumber: string;
  bankAgency: string;
  bankAccountNumber: string;
  bankDigit: string;
  bankType: string;
  gender: string;
  addressStreet1: string;
  addressNeigh1: string;
  addressCity1: string;
  addressStateCode1: string;
  addressZipCode1: string;
  mothersName: string;
  sellerCode: string;
  masterCode: string;
}

export function ModalDigitacaoConsigCLT({
  onChangeTab,
}: {
  onChangeTab?: (tabKey: AttendanceTabKey) => void;
}) {
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [digitacaoFields, errors, onChangeField, setFields, setErrors] =
    useFormFields<DigitacaoCLTProps>();
  const [usuariosDigitadores, setUsuariosDigitadores] = useState<
    { name: string; value: string; completedUser: object }[]
  >([]);
  const { isOpen, onClose, params } = useModalControls<ModalParams>(eventName, {
    onOpen: async (params) => {
      if (!params) throw new Error("Insira um banco digitação.");
      if (params.digitado) {
        const digitacaoData = await getConsigCLTDigitacaoData(params.digitado);
        setFields({
          ...digitacaoData,
          bancoDigitacao: params.bancoDigitacao,
        });
      } else {
        setFields({
          ...digitacaoFields,
          bancoDigitacao: params.bancoDigitacao,
          ...copyAttendanceDataToDigitacaoConsigCLT(attendance),
        });
      }

      getUsuariosDigitadores(params.bancoDigitacao);
    },
    onClose() {
      setFields({});
      setErrors({});
    },
  });
  const { formControl, isReadOnly } = useAttendanceContext();
  const { user } = useApplicationContext();
  const attendance = formControl.values as Attendance;
  const isGerente = isUserGerente(user);
  const allDisabled = isReadOnly || !!params.digitado;

  const handleSubmit = async () => {
    const { body, isValid, errors } = validateDigitacaoCLT(digitacaoFields);

    if (!isValid) {
      console.log("ERRORS => ", errors);
      return setErrors(errors);
    }

    setIsLoading(true);
    try {
      const { data } = await apiDigitacao.post(
        `/api/digitacoes/clt/atendimento/${attendance.id}/novo/digitar`,
        body
      );
      onClose();
      onChangeTab?.("digitados");
    } catch (e: any) {
      const description = e?.response?.data?.message;
      Toast({ title: `Erro ao digitar`, description });
    } finally {
      setIsLoading(false);
    }
  };

  const getConsigCLTDigitacaoData = async (digitadoRow: DigitadoRow) => {
    setIsLoadingModal(true);
    try {
      const { data } = await apiDigitacao.get(
        `/api/digitacoes/clt/formulario/${digitadoRow.id}`
      );
      return data;
    } catch (e) {
      Toast({ title: `Erro ao obter dados de digitação.` });
      return {};
    } finally {
      setIsLoadingModal(false);
    }
  };

  const setCpfData = (cep: CEPProps) => {
    onChangeField("addressStreet1", cep.logradouro);
    onChangeField("addressNeigh1", cep.bairro);

    const possiveisCidades = factaCitiesArray.filter((crr) => {
      return (
        crr.value.uf === cep.uf &&
        normalizeText(crr.value.cityName).includes(
          normalizeText(cep.localidade!)
        )
      );
    });
    if (possiveisCidades.length === 1) {
      onChangeField("addressCity1", possiveisCidades[0].value.cityName);
      onChangeField("addressStateCode1", possiveisCidades[0].value.uf);
    }
  };

  const loadCEPData = async (cep: string) => {
    const { error, errorMessage, ...cpfData } = await getCEPData(cep);
    if (error) {
      Toast({ title: errorMessage, status: "error" });
    } else {
      setCpfData(cpfData as CEPProps);
    }
  };

  async function getUsuariosDigitadores(bancoDigitacao: string) {
    setIsLoading(true);
    const banco = bancoDigitacao === "FACTA_PINE" ? "FACTA" : bancoDigitacao;
    try {
      const { data }: { data: { username: string; apiKey: string }[] } =
        await api.get(`/usuarios-bancos/digitadores/${banco}`, {
          params: {
            customer_id: isGerente ? attendance.customer.id : undefined,
          },
        });
      setUsuariosDigitadores(
        data.map((u) => ({
          name: u.username,
          value: u.username,
          completedUser: u,
        }))
      );
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }

  function phoneDDDMask(value: any) {
    const ddd = digitacaoFields["phoneDDD1"] ?? "";
    const result = maskPhoneFormat(ddd + value);
    return { raw: "", value: result.formatedValue };
  }

  const modalFooter = (
    <>
      <Center>
        <Text mr="12px">Usuário digitador banco: </Text>
        <DropdownField
          onChange={(value, option) => {
            const user = option.completedUser;
            onChangeField("sellerCode", value);
            onChangeField("masterCode", user.codigoPromotora);
          }}
          dropdownProps={{
            w: "300px",
            children: allDisabled ? digitacaoFields.sellerCode : undefined,
          }}
          value={digitacaoFields.sellerCode}
          options={usuariosDigitadores}
          errorMessage={errors["sellerCode"] || errors["masterCode"]}
          isDisabled={isLoading || allDisabled}
        />
      </Center>
      <Button
        onClick={() => handleSubmit()}
        isLoading={isLoading}
        loadingText="Digitando"
        isDisabled={allDisabled}
      >
        Digitar
      </Button>

      <Button variant="outline" onClick={onClose} leftIcon={<CloseIcon />}>
        Cencelar
      </Button>
    </>
  );

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle={`Digitar Consignado CLT ${params.bancoDigitacao}`}
      modalFooter={modalFooter}
      size="4xl"
      scroll="inside"
      closeOnOverlayClick={false}
      isLoading={isLoadingModal}
    >
      <Grid
        templateColumns="1fr"
        gap="16px"
        border="1px solid var(--chakra-colors-custom-gray)"
        bg="var(--chakra-colors-gray-100)"
        p="20px 10px 10px 10px"
        borderRadius="4px"
      >
        <Flex pos="relative" mb="10px">
          <Center w="100%">
            <Text fontSize="20px" fontWeight="medium">
              Formulário{" "}
              {bancosConsigCLT.find(
                ({ value }) => value === params.bancoDigitacao
              )?.name || params.bancoDigitacao}
            </Text>
          </Center>
        </Flex>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Nome"
            onChange={(e) => onChangeField("name", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["name"]}
            errorMessage={errors["name"]}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="CPF"
            onChange={(e) =>
              onChangeField("cpf", maskCPFFormat(e.target.value).raw)
            }
            inputProps={{ bg: "#fff" }}
            value={maskCPFFormat(digitacaoFields["cpf"]).value}
            errorMessage={errors["cpf"]}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Celular"
            onChange={(e) => {
              const result = maskPhoneFormat(e.target.value);
              const ddd = result.parts[1];
              const numberPart1 = result.parts[2];
              const numberPart2 = result.parts[3];
              const number = numberPart1 + numberPart2;
              onChangeField("phoneNumber1", number);
              onChangeField("phoneDDD1", ddd);
            }}
            inputProps={{ bg: "#fff" }}
            value={phoneDDDMask(digitacaoFields["phoneNumber1"]).value}
            errorMessage={errors["phoneNumber1"]}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Número do Documento"
            onChange={(e) => onChangeField("documentNumber", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["documentNumber"]}
            errorMessage={errors["documentNumber"]}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Código Banco"
            onChange={(e) => onChangeField("bankNumber", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["bankNumber"]}
            errorMessage={errors["bankNumber"]}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Agência"
            onChange={(e) => onChangeField("bankAgency", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["bankAgency"]}
            errorMessage={errors["bankAgency"]}
          />
        </Grid>
        <Grid
          templateColumns="220px auto 67px 15%"
          alignItems="center"
          gap="20px"
        >
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Conta"
            onChange={(e) => onChangeField("bankAccountNumber", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["bankAccountNumber"]}
            errorMessage={errors["bankAccountNumber"]}
          />
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Díg. Verif."
            onChange={(e) => onChangeField("bankDigit", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["bankDigit"]}
            errorMessage={errors["bankDigit"]}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <DropdownField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            dropdownProps={{ w: "100%", bg: "#fff" }}
            title="Tipo Conta"
            onChange={(value) => onChangeField("bankType", value)}
            value={digitacaoFields["bankType"]}
            errorMessage={errors["bankType"]}
            options={dropdownTipoConta}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <DropdownField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            dropdownProps={{ w: "100%", bg: "#fff" }}
            title="Sexo"
            onChange={(value) => onChangeField("gender", value)}
            value={digitacaoFields["gender"]}
            errorMessage={errors["gender"]}
            options={[
              { name: "Feminino", value: "F" },
              { name: "Masculino", value: "M" },
            ]}
          />
        </Grid>
        <Grid templateColumns="220px auto 140px" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="CEP"
            onChange={(e) =>
              onChangeField(
                "addressZipCode1",
                maskCEPFormat(e.target.value).raw
              )
            }
            inputProps={{
              bg: "#fff",
              onKeyDown: (e) => {
                if (e.key === "Enter") {
                  loadCEPData(e.currentTarget.value.replace(/\D/g, ""));
                }
              },
            }}
            value={maskCEPFormat(digitacaoFields["addressZipCode1"]).value}
            errorMessage={errors["addressZipCode1"]}
          />
          <ButtonWithLoading
            isDisabled={
              (digitacaoFields["addressZipCode1"]?.length ?? 0) < 8 ||
              allDisabled
            }
            onClick={async () =>
              loadCEPData(digitacaoFields["addressZipCode1"]!)
            }
            rightIcon={<Search2Icon />}
          >
            Buscar CEP
          </ButtonWithLoading>
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Rua"
            onChange={(e) => onChangeField("addressStreet1", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["addressStreet1"]}
            errorMessage={errors["addressStreet1"]}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Bairro"
            onChange={(e) => onChangeField("addressNeigh1", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["addressNeigh1"]}
            errorMessage={errors["addressNeigh1"]}
          />
        </Grid>
        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <Text fontWeight="medium" textAlign="end">
            Cidade
          </Text>
          <InputSearch
            onChange={(optionValue: { cityName: string; uf: string }) => {
              onChangeField("addressCity1", optionValue.cityName);
              onChangeField("addressStateCode1", optionValue.uf);
            }}
            clearInput={false}
            searchKeys={["name"]}
            errorMessage={errors["addressCity1"]}
            checkIsInclude={(option) =>
              digitacaoFields["addressCity1"] === option.value.cityName
            }
            optionLabelRender={(option) =>
              `${option.name} - ${option.value.uf}`
            }
            value={
              digitacaoFields["addressCity1"] &&
              digitacaoFields["addressStateCode1"]
                ? `${digitacaoFields["addressCity1"]} - ${digitacaoFields["addressStateCode1"]}`
                : undefined
            }
            options={factaCitiesArray}
            inputProps={{
              id: "InputSearch-addressCity1",
              bg: "#fff",
              onBlur: (e) => {
                if (
                  digitacaoFields["addressStateCode1"] &&
                  digitacaoFields["addressCity1"]
                )
                  e.target.value = `${digitacaoFields["addressCity1"]} - ${digitacaoFields["addressStateCode1"]}`;
                else e.target.value = "";
              },
              isDisabled: allDisabled,
            }}
          />
        </Grid>

        <Grid templateColumns="220px auto" alignItems="center" gap="20px">
          <InputField
            isDisabled={allDisabled}
            titleProps={{ textAlign: "end", mb: "0px" }}
            title="Nome Mãe"
            onChange={(e) => onChangeField("mothersName", e.target.value)}
            inputProps={{ bg: "#fff" }}
            value={digitacaoFields["mothersName"]}
            errorMessage={errors["mothersName"]}
          />
        </Grid>
      </Grid>
    </CustomModal>
  );
}

export const openModalDigitacaoConsigCLT =
  createModalTrigger<ModalParams>(eventName);
